import { cn } from "../utils/cx.js";

export interface ProgressProps {
  percent: number;
  type?: "line" | "circle";
  /** "neutral" draws the track in ink instead of a hue — for a meter that must not read as a status. */
  status?: "normal" | "success" | "error" | "active" | "neutral";
  showInfo?: boolean;
  size?: number;
  strokeWidth?: number;
  className?: string;
}

// Success takes the app's status green (--az-success), not Tailwind's emerald,
// so a completed bar matches the "published/active" state everywhere else.
const STROKE = {
  normal: "stroke-primary",
  active: "stroke-primary",
  success: "stroke-[var(--az-success)]",
  error: "stroke-destructive",
  neutral: "stroke-foreground",
} as const;

const FILL = {
  normal: "bg-primary",
  active: "bg-primary",
  success: "bg-[var(--az-success)]",
  error: "bg-destructive",
  neutral: "bg-foreground",
} as const;

export function Progress({
  percent,
  type = "line",
  status = "normal",
  showInfo = true,
  size,
  strokeWidth,
  className,
}: ProgressProps) {
  const pct = Math.max(0, Math.min(100, percent));

  if (type === "circle") {
    const sz = size ?? 120;
    const sw = strokeWidth ?? 6;
    const r = (sz - sw) / 2;
    const circ = 2 * Math.PI * r;
    const offset = circ * (1 - pct / 100);
    return (
      <div
        className={cn("relative inline-flex items-center justify-center", className)}
        style={{ width: sz, height: sz }}
      >
        <svg width={sz} height={sz} viewBox={`0 0 ${sz} ${sz}`} className="-rotate-90">
          <circle cx={sz / 2} cy={sz / 2} r={r} fill="none" strokeWidth={sw} className="stroke-muted" />
          <circle
            cx={sz / 2}
            cy={sz / 2}
            r={r}
            fill="none"
            strokeWidth={sw}
            strokeLinecap="round"
            strokeDasharray={circ}
            strokeDashoffset={offset}
            className={cn("transition-[stroke-dashoffset]", STROKE[status])}
          />
        </svg>
        {showInfo && <span className="absolute text-sm font-semibold tabular-nums">{Math.round(pct)}%</span>}
      </div>
    );
  }

  return (
    <div className={cn("flex items-center gap-3", className)}>
      <div
        className="h-2 w-full overflow-hidden rounded-full bg-muted"
        style={strokeWidth ? { height: strokeWidth } : undefined}
      >
        <div
          className={cn("h-full rounded-full transition-[width]", FILL[status])}
          style={{ width: `${pct}%` }}
        />
      </div>
      {showInfo && (
        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{Math.round(pct)}%</span>
      )}
    </div>
  );
}
